/**
 * SocialMediaDashboardService
 * Service for social media dashboard analytics
 * Route: api/socialmedia/dashboard
 */
import { BaseService } from '../base/BaseService';
/**
 * SocialMediaDashboardService
 * Handles dashboard-level analytics and aggregated metrics
 */
export class SocialMediaDashboardService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? SocialMediaDashboardService.DEFAULT_SERVICE_PATH,
            basePath: SocialMediaDashboardService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET OVERVIEW ====================
    /**
     * Get dashboard overview
     * GET api/socialmedia/dashboard/overview?accountId={accountId}
     */
    async getOverview(accountId) {
        const response = await this.getOverviewRaw(accountId);
        if (!response.data) {
            throw new Error('Failed to retrieve dashboard overview');
        }
        return response.data;
    }
    /**
     * Get dashboard overview with full response
     */
    getOverviewRaw(accountId) {
        const endpoint = this.getEndpoint('overview', 'overview');
        const url = this.buildUrl(endpoint);
        const urlWithParams = accountId ? `${url}?accountId=${accountId}` : url;
        return this.http.get(urlWithParams);
    }
    // ==================== GET ENGAGEMENT TRENDS ====================
    /**
     * Get engagement trends
     * GET api/socialmedia/dashboard/engagement-trends?accountId={accountId}&days={days}
     */
    async getEngagementTrends(accountId, days = 30) {
        const response = await this.getEngagementTrendsRaw(accountId, days);
        return response.data ?? [];
    }
    /**
     * Get engagement trends with full response
     */
    getEngagementTrendsRaw(accountId, days = 30) {
        const endpoint = this.getEndpoint('engagement-trends', 'engagement-trends');
        const url = this.buildUrl(endpoint);
        return this.http.get(`${url}?accountId=${accountId}&days=${days}`);
    }
    // ==================== GET FOLLOWER GROWTH ====================
    /**
     * Get follower growth data
     * GET api/socialmedia/dashboard/follower-growth?accountId={accountId}&days={days}
     */
    async getFollowerGrowth(accountId, days = 30) {
        const response = await this.getFollowerGrowthRaw(accountId, days);
        return response.data ?? {};
    }
    /**
     * Get follower growth with full response
     */
    getFollowerGrowthRaw(accountId, days = 30) {
        const endpoint = this.getEndpoint('follower-growth', 'follower-growth');
        const url = this.buildUrl(endpoint);
        return this.http.get(`${url}?accountId=${accountId}&days=${days}`);
    }
    // ==================== GET PLATFORM COMPARISON ====================
    /**
     * Get platform comparison data
     * GET api/socialmedia/dashboard/platform-comparison?days={days}
     */
    async getPlatformComparison(days = 30) {
        const response = await this.getPlatformComparisonRaw(days);
        return response.data ?? {};
    }
    /**
     * Get platform comparison with full response
     */
    getPlatformComparisonRaw(days = 30) {
        const endpoint = this.getEndpoint('platform-comparison', 'platform-comparison');
        const url = this.buildUrl(endpoint);
        return this.http.get(`${url}?days=${days}`);
    }
    // ==================== GET CONTENT PERFORMANCE ====================
    /**
     * Get content performance data
     * GET api/socialmedia/dashboard/content-performance?accountId={accountId}&limit={limit}
     */
    async getContentPerformance(accountId, limit = 10) {
        const response = await this.getContentPerformanceRaw(accountId, limit);
        return response.data ?? {};
    }
    /**
     * Get content performance with full response
     */
    getContentPerformanceRaw(accountId, limit = 10) {
        const endpoint = this.getEndpoint('content-performance', 'content-performance');
        const url = this.buildUrl(endpoint);
        return this.http.get(`${url}?accountId=${accountId}&limit=${limit}`);
    }
    // ==================== GET VIDEO PERFORMANCE ====================
    /**
     * Get video performance data
     * GET api/socialmedia/dashboard/video-performance?accountId={accountId}&limit={limit}
     */
    async getVideoPerformance(accountId, limit = 10) {
        const response = await this.getVideoPerformanceRaw(accountId, limit);
        return response.data ?? {};
    }
    /**
     * Get video performance with full response
     */
    getVideoPerformanceRaw(accountId, limit = 10) {
        const endpoint = this.getEndpoint('video-performance', 'video-performance');
        const url = this.buildUrl(endpoint);
        return this.http.get(`${url}?accountId=${accountId}&limit=${limit}`);
    }
    // ==================== GET ADS PERFORMANCE ====================
    /**
     * Get ads performance data
     * GET api/socialmedia/dashboard/ads-performance?accountId={accountId}
     */
    async getAdsPerformance(accountId) {
        const response = await this.getAdsPerformanceRaw(accountId);
        return response.data ?? {};
    }
    /**
     * Get ads performance with full response
     */
    getAdsPerformanceRaw(accountId) {
        const endpoint = this.getEndpoint('ads-performance', 'ads-performance');
        const url = this.buildUrl(endpoint);
        return this.http.get(`${url}?accountId=${accountId}`);
    }
    // ==================== GET ROI SUMMARY ====================
    /**
     * Get ROI summary
     * GET api/socialmedia/dashboard/roi-summary?accountId={accountId}&days={days}
     */
    async getROISummary(accountId, days = 30) {
        const response = await this.getROISummaryRaw(accountId, days);
        return response.data ?? {};
    }
    /**
     * Get ROI summary with full response
     */
    getROISummaryRaw(accountId, days = 30) {
        const endpoint = this.getEndpoint('roi-summary', 'roi-summary');
        const url = this.buildUrl(endpoint);
        return this.http.get(`${url}?accountId=${accountId}&days=${days}`);
    }
    // ==================== GET AUDIENCE INSIGHTS ====================
    /**
     * Get audience insights summary
     * GET api/socialmedia/dashboard/audience-insights?accountId={accountId}&days={days}
     */
    async getAudienceInsightsSummary(accountId, days = 30) {
        const response = await this.getAudienceInsightsSummaryRaw(accountId, days);
        return response.data ?? {};
    }
    /**
     * Get audience insights summary with full response
     */
    getAudienceInsightsSummaryRaw(accountId, days = 30) {
        const endpoint = this.getEndpoint('audience-insights', 'audience-insights');
        const url = this.buildUrl(endpoint);
        return this.http.get(`${url}?accountId=${accountId}&days=${days}`);
    }
}
SocialMediaDashboardService.DEFAULT_SERVICE_PATH = 'social-media';
SocialMediaDashboardService.BASE_PATH = 'api/socialmedia/dashboard';
//# sourceMappingURL=SocialMediaDashboardService.js.map